import type { MediaState, PeerInfo } from "../types";
import type { RoomPeer } from "../hooks/useMeetingRoom";
import { VideoTile } from "./VideoTile";

interface Props {
  me: PeerInfo | null;
  localCameraStream: MediaStream | null;
  localScreenStream: MediaStream | null;
  localMediaState: MediaState;
  peers: RoomPeer[];
  speakerId?: string | null;
  /** Persist tile playback diagnostics (autoplay/sink failures) in the session log. */
  onDiagnostic?: (type: string, payload?: Record<string, unknown>) => void;
}

/**
 * Adaptive tile grid. Any active screen share (remote or local) is promoted to
 * a full-width spotlight above the camera tiles. Each peer has a single video
 * channel (camera OR screen) — media_state.screen tells which.
 */
export function VideoTileGrid({
  me,
  localCameraStream,
  localScreenStream,
  localMediaState,
  peers,
  speakerId = null,
  onDiagnostic,
}: Props) {
  const screenPeer = peers.find((p) => p.mediaState.screen && p.stream);
  const spotlightStream = screenPeer?.stream ?? localScreenStream;
  const spotlightName = screenPeer
    ? `${screenPeer.name} — ekran`
    : localScreenStream
      ? "Twój ekran"
      : null;

  // The peer whose screen is in the spotlight is excluded from the small strip
  // (their one video channel is already shown big).
  const stripPeers = screenPeer
    ? peers.filter((p) => p.peerId !== screenPeer.peerId)
    : peers;

  const tileCount = stripPeers.length + 1;
  const cols =
    tileCount <= 1
      ? "grid-cols-1"
      : tileCount <= 4
        ? "grid-cols-1 sm:grid-cols-2"
        : tileCount <= 9
          ? "grid-cols-2 md:grid-cols-3"
          : "grid-cols-3 md:grid-cols-4";
  // Cap the grid width so a 1–2 person call doesn't stretch a single tile
  // across an ultra-wide screen; more people widen the cap.
  const maxW =
    tileCount <= 1
      ? "max-w-2xl"
      : tileCount <= 2
        ? "max-w-4xl"
        : tileCount <= 4
          ? "max-w-5xl"
          : "max-w-full";

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-2">
      {spotlightStream && spotlightName && (
        <div className="min-h-0 flex-[3]">
          <VideoTile
            stream={spotlightStream}
            name={spotlightName}
            muted={!screenPeer}
            isScreen
            spotlight
            sinkId={speakerId}
            onDiagnostic={onDiagnostic}
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto">
        <div className={`grid w-full gap-2 ${maxW} ${cols}`}>
          <VideoTile
            stream={localCameraStream}
            name={`${me?.name ?? "Ja"} (Ty)`}
            muted
            mirrored
            micOn={localMediaState.audio}
            // While screen-sharing, the video channel is the screen → self camera
            // tile shows the avatar.
            cameraOff={!localMediaState.video || localMediaState.screen}
          />
          {stripPeers.map((peer) => (
            <VideoTile
              key={peer.peerId}
              stream={peer.stream}
              name={peer.isGuest ? `${peer.name} (gość)` : peer.name}
              micOn={peer.mediaState.audio}
              cameraOff={!peer.mediaState.video && !peer.mediaState.screen}
              reconnecting={
                peer.connectionState === "disconnected" ||
                peer.connectionState === "checking" ||
                peer.connectionState === "new"
              }
              failed={peer.failed}
              sinkId={speakerId}
              onDiagnostic={onDiagnostic}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
